import { ExternalLink } from "lucide-mui";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { keys } from "../../../api/queryKeys.ts";
import { api } from "../../../api/client.ts";
import FormattedDate from "../../../components/FormattedDate.tsx";
import Panel from "../../../components/Panel.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import { humanize } from "../../../utils/format.ts";

interface NewsMentionsSectionProps {
  companyId: string;
}

// News articles signal research found this company in, across every tenant.
export default function NewsMentionsSection({ companyId }: NewsMentionsSectionProps) {
  const { data, isError } = useQuery({
    queryKey: keys.masterCompany.newsMentions(companyId),
    queryFn: () => api.getCompanyNewsMentions(companyId, { limit: 100 }),
    retry: false,
  });

  if (isError || !data || data.items.length === 0) return null;

  return (
    <Panel title="News mentions" count={data.total} sx={{ mb: 3 }}>
      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Published</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.items.map((mention) => (
            <TableRow key={mention.article_id}>
              <TableCell>
                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                  <Link component={RouterLink} to={`/news-articles/${mention.article_id}`}>
                    {mention.title || mention.document_url}
                  </Link>
                  <IconButton
                    size="small"
                    href={mention.document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink fontSize="inherit" />
                  </IconButton>
                </Stack>
              </TableCell>
              <TableCell>{mention.source_common_name ?? "-"}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
                  {mention.roles.map((role) => (
                    <Chip key={role} size="small" label={humanize(role)} />
                  ))}
                </Stack>
              </TableCell>
              <TableCell>
                <FormattedDate value={mention.published_at} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </ScrollTable>
    </Panel>
  );
}
